import { ref, onMounted, onUnmounted, computed } from 'vue'
import { fetchLayoutFromOpenRouter } from '@/services/openrouter'

const CACHE_DURATION = 12 * 60 * 60 * 1000 // 12 hours in milliseconds
const CACHE_KEY_PREFIX = 'ai-layout-cache-'

  // Default layout configuration
const defaultLayout = {
  gridHeight: '70vh',
  gridWidth: '90%',
  cardGap: '24px',
  titleSize: '3.5rem',
  padding: '60px 40px',
  gridTemplate: '2x2',
  gridColumns: 'repeat(2, 1fr)',
  gridRows: 'repeat(2, 1fr)',
  centered: true
}

export function useAILayout() {
  // Reactive state
  const layoutConfig = ref({ ...defaultLayout })
  const isLoading = ref(false)
  const error = ref(null)
  const screenInfo = ref({
    width: window.innerWidth,
    height: window.innerHeight,
    ratio: window.innerWidth / window.innerHeight,
    dpr: window.devicePixelRatio
  })

  // Computed styles for binding
  const containerStyle = computed(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: layoutConfig.value.centered !== false ? 'center' : 'flex-start',
    alignItems: 'center',
    minHeight: '100vh',
    padding: layoutConfig.value.padding
  }))

  const titleStyle = computed(() => ({
    fontSize: layoutConfig.value.titleSize,
    marginBottom: 'clamp(20px, 4vh, 60px)'
  }))

  const gridStyle = computed(() => ({
    display: 'grid',
    gridTemplateColumns: layoutConfig.value.gridColumns,
    gridTemplateRows: layoutConfig.value.gridRows,
    gap: layoutConfig.value.cardGap,
    width: layoutConfig.value.gridWidth,
    height: layoutConfig.value.gridHeight,
    maxHeight: '1400px',
    margin: '0 auto'
  }))

  // Generate cache key from screen dimensions
  const getCacheKey = () => {
    const { width, height } = screenInfo.value
    return `${CACHE_KEY_PREFIX}${width}x${height}`
  }

  // Check if cached layout is valid
  const getCachedLayout = () => {
    try {
      const cacheKey = getCacheKey()
      const cached = localStorage.getItem(cacheKey)
      
      if (!cached) return null
      
      const { layout, timestamp } = JSON.parse(cached)
      const age = Date.now() - timestamp
      
      // Check if cache is still valid (12 hours)
      if (age < CACHE_DURATION) {
        console.log('Using cached layout for', cacheKey)
        return layout
      }
      
      // Cache expired, remove it
      localStorage.removeItem(cacheKey)
      return null
    } catch (err) {
      console.error('Error reading cache:', err)
      return null
    }
  }

  // Save layout to cache
  const setCachedLayout = (layout) => {
    try {
      const cacheKey = getCacheKey()
      const cacheData = {
        layout,
        timestamp: Date.now()
      }
      localStorage.setItem(cacheKey, JSON.stringify(cacheData))
      console.log('Cached layout for', cacheKey)
    } catch (err) {
      console.error('Error saving cache:', err)
    }
  }

  // Get last successful layout from any cache
  const getLastSuccessfulLayout = () => {
    try {
      // Get all cache keys
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(CACHE_KEY_PREFIX)
      )
      
      if (keys.length === 0) return null
      
      // Find most recent cache entry
      let mostRecent = null
      let mostRecentTime = 0
      
      keys.forEach(key => {
        try {
          const cached = JSON.parse(localStorage.getItem(key))
          if (cached.timestamp > mostRecentTime) {
            mostRecentTime = cached.timestamp
            mostRecent = cached.layout
          }
        } catch (e) {
          // Ignore invalid cache entries
        }
      })
      
      return mostRecent
    } catch (err) {
      console.error('Error reading last successful layout:', err)
      return null
    }
  }

  // Clear cache for current screen size to force fresh AI call
  const clearCurrentCache = () => {
    try {
      const cacheKey = getCacheKey()
      localStorage.removeItem(cacheKey)
      console.log('Cleared cache for', cacheKey)
    } catch (err) {
      console.error('Error clearing cache:', err)
    }
  }

  // Force refresh - clear cache and fetch new layout
  const forceRefresh = async () => {
    clearCurrentCache()
    await fetchLayout()
  }

  // Parse grid template and set columns/rows
  const parseGridTemplate = (template) => {
    switch (template) {
      case '1x4':
        return {
          gridColumns: '1fr',
          gridRows: 'repeat(4, 1fr)'
        }
      case '4x1':
        return {
          gridColumns: 'repeat(4, 1fr)',
          gridRows: '1fr'
        }
      case '2x2':
      default:
        return {
          gridColumns: 'repeat(2, 1fr)',
          gridRows: 'repeat(2, 1fr)'
        }
    }
  }

  // Apply layout configuration
  const applyLayout = (config) => {
    const gridSettings = parseGridTemplate(config.gridTemplate)
    
    layoutConfig.value = {
      ...config,
      ...gridSettings
    }
    
    console.log('Applied AI layout:', layoutConfig.value)
  }

  // Fetch layout from AI
  const fetchLayout = async () => {
    // Check cache first
    const cached = getCachedLayout()
    if (cached) {
      applyLayout(cached)
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const aiResponse = await fetchLayoutFromOpenRouter(screenInfo.value)
      
      if (aiResponse && aiResponse.gridHeight) {
        applyLayout(aiResponse)
        setCachedLayout(aiResponse)
      } else {
        throw new Error('Invalid AI response')
      }
    } catch (err) {
      console.error('Error fetching layout from AI:', err)
      error.value = err.message
      
      // Fallback to last successful layout or defaults
      const lastLayout = getLastSuccessfulLayout()
      if (lastLayout) {
        console.log('Using last successful layout')
        applyLayout(lastLayout)
      } else {
        console.log('Using default layout')
        applyLayout(defaultLayout)
      }
    } finally {
      isLoading.value = false
    }
  }

  // Update screen info
  const updateScreenInfo = () => {
    screenInfo.value = {
      width: window.innerWidth,
      height: window.innerHeight,
      ratio: window.innerWidth / window.innerHeight,
      dpr: window.devicePixelRatio
    }
  }

  // Debounce function
  const debounce = (fn, delay) => {
    let timeoutId
    return (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn(...args), delay)
    }
  }

  // Handle resize with debounce
  const handleResize = debounce(() => {
    const oldWidth = screenInfo.value.width
    const oldHeight = screenInfo.value.height
    
    updateScreenInfo()
    
    const { width, height } = screenInfo.value
    
    // Only refetch if dimensions changed significantly (>50px)
    if (Math.abs(width - oldWidth) > 50 || Math.abs(height - oldHeight) > 50) {
      console.log('Screen size changed significantly, fetching new layout...')
      fetchLayout()
    }
  }, 500)

  // Lifecycle hooks
  onMounted(() => {
    updateScreenInfo()
    fetchLayout()
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
  })

  return {
    layoutConfig,
    isLoading,
    error,
    screenInfo,
    containerStyle,
    titleStyle,
    gridStyle,
    refreshLayout: fetchLayout,
    forceRefresh
  }
}